import { useEffect, useState } from "react";
import { isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { MAX_COST_INDEX, MEDIAN_COST_INDEX, MIN_COST_INDEX } from "./constants";
import PriceAdvisorRepairsLink from "./PriceAdvisorRepairsLink";
import PriceLine from "./PriceLine";
import PriceTitle from "./PriceTitle";
import RoomForImprovementBanner from "./RoomForImprovementBanner";
import {
  OtherRecommendation,
  RepairRecommendation,
  ServiceCenter,
} from "./types";
import {
  buildRepairOtherFilter,
  getVendorLocation,
  getVendorName,
} from "./utils";

interface Props {
  otherRecommendation: OtherRecommendation;
  recommendation: RepairRecommendation;
  serviceCentersByID: Record<string, ServiceCenter>;
  serviceCenterCurrent: string;
  assetFilters: string;
  workOrderID: string;
  workOrderDate: string;
  consideredHistoryDays: number;
  partVMRSIDs: string[];
  laborVMRSIDs: string[];
  isStreaming: boolean;
  hasAssetData: boolean;
}

const OtherRecommendationSection = ({
  otherRecommendation,
  recommendation,
  serviceCentersByID,
  serviceCenterCurrent,
  assetFilters,
  workOrderID,
  workOrderDate,
  consideredHistoryDays,
  partVMRSIDs,
  laborVMRSIDs,
  isStreaming,
  hasAssetData,
}: Props) => {
  const { t } = useTranslation("duck");
  const [isExpanded, setIsExpanded] = useState(
    otherRecommendation.room_for_improvement
  );

  useEffect(() => {
    setIsExpanded(otherRecommendation.room_for_improvement);
  }, [otherRecommendation.room_for_improvement]);

  if (!otherRecommendation.service_center_alternatives) {
    return null;
  }

  const hasExpandableContent =
    otherRecommendation.service_center_alternatives.length > 0;

  const minimum = Math.min(
    ...otherRecommendation.service_center_alternatives.map(
      (alternative) =>
        alternative.cost_min_median_max?.[MIN_COST_INDEX] ?? Infinity
    ),
    otherRecommendation.cost_other ?? Infinity
  );

  const maximum = Math.max(
    ...otherRecommendation.service_center_alternatives.map(
      (alternative) =>
        alternative.cost_min_median_max?.[MAX_COST_INDEX] ?? -Infinity
    ),
    otherRecommendation.cost_other ?? -Infinity
  );

  const parsedWorkOrderDate = workOrderDate
    ? parseISO(workOrderDate)
    : undefined;
  const canBuildLinks =
    hasAssetData &&
    Boolean(workOrderDate) &&
    Boolean(workOrderID) &&
    Number.isFinite(consideredHistoryDays) &&
    Boolean(parsedWorkOrderDate) &&
    isValid(parsedWorkOrderDate);

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="ml-4 text-xs">
            <div className="font-semibold">{t("other")}</div>
          </div>
        </TableCell>
        <TableCell>
          <PriceTitle
            minimum={minimum}
            maximum={maximum}
            quoted={otherRecommendation.cost_other}
            currencyCode={recommendation.currency_code}
            hasExpandableContent={hasExpandableContent}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
            isExpanded={isExpanded}
          />
        </TableCell>
      </TableRow>
      {otherRecommendation.room_for_improvement && (
        <TableRow
          sx={{
            display: isExpanded ? "table-row" : "none",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 300ms",
          }}
        >
          <TableCell colSpan={2} sx={{ p: 0, borderBottom: "none" }}>
            <RoomForImprovementBanner />
          </TableCell>
        </TableRow>
      )}
      {otherRecommendation.service_center_alternatives.map(
        (alternative, index) => (
          <TableRow
            key={`${alternative.service_center_id || "aggregated"}-${index}`}
            sx={{
              display: isExpanded ? "table-row" : "none",
              opacity: isExpanded ? 1 : 0,
              transition: "opacity 300ms",
            }}
          >
            <TableCell>
              <div className="ml-4 text-xs">
                <div className="flex space-x-2">
                  <span className="font-semibold">
                    {getVendorName(
                      serviceCentersByID,
                      t,
                      alternative.service_center_id,
                      isStreaming
                    )}
                  </span>
                  {canBuildLinks && (
                    <PriceAdvisorRepairsLink
                      repairFilter={buildRepairOtherFilter({
                        part_vmrs_ids: partVMRSIDs,
                        labor_vmrs_ids: laborVMRSIDs,
                        description: recommendation.description,
                        work_order_date: workOrderDate,
                        work_order_id: workOrderID,
                        considered_history_days: consideredHistoryDays,
                        min_threshold_cost_other:
                          otherRecommendation.min_threshold_cost_other,
                        max_threshold_cost_other:
                          otherRecommendation.max_threshold_cost_other,
                        brand_name: alternative.service_center_id
                          ? serviceCentersByID[alternative.service_center_id]
                              ?.brand_name
                          : undefined,
                      })}
                      repairCount={alternative.repairs_count}
                      assetFilter={assetFilters}
                    />
                  )}
                </div>
                <div className="text-xs text-neutral-500">
                  {getVendorLocation(
                    serviceCentersByID,
                    serviceCenterCurrent,
                    t,
                    alternative.service_center_id
                  )}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <PriceLine
                quoted={otherRecommendation.cost_other}
                minimum={minimum}
                maximum={maximum}
                current={alternative?.cost_min_median_max?.[MEDIAN_COST_INDEX]!}
                currentMin={alternative?.cost_min_median_max?.[MIN_COST_INDEX]}
                currentMax={alternative?.cost_min_median_max?.[MAX_COST_INDEX]}
                currencyCode={recommendation.currency_code}
              />
            </TableCell>
          </TableRow>
        )
      )}
    </>
  );
};

export default OtherRecommendationSection;
